import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Search, Users } from "lucide-react";
import { debounce } from "lodash";
import { useAuth } from "../../global-components/src";
import { api } from "../../services/api";
import { getImageUser, getImageUserNotFound } from "../../global-components/src/utils/auth";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { User } from "../../global-components/src/types";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { useTranslation } from "react-i18next";

interface Props {
    show: boolean;
    onClose: () => void;
    onNewGroup: () => void;
}

const NewConversationPanel = ({ show, onClose, onNewGroup }: Props) => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [rendered, setRendered] = useState(show);
    const [entering, setEntering] = useState(false);
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<User[]>([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (show) {
            setRendered(true);
            const raf = requestAnimationFrame(() => {
                setEntering(true);
                setTimeout(() => inputRef.current?.focus(), 80);
            });
            return () => cancelAnimationFrame(raf);
        } else {
            setEntering(false);
            const t = setTimeout(() => {
                setRendered(false);
                setSearch("");
                setResults([]);
            }, 220);
            return () => clearTimeout(t);
        }
    }, [show]);

    useEffect(() => {
        if (!show) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [show, onClose]);

    const doSearch = useCallback(
        debounce(async (q: string) => {
            setLoadingSearch(true);
            try {
                const res = await api(token).get(`${PACOCA_API_URL}/search`, {
                    params: { page: 0, typeSeach: "user", search: q || "" },
                });
                setResults(res.data?.users ?? res.data ?? []);
            } catch {
                setResults([]);
            } finally {
                setLoadingSearch(false);
            }
        }, 350),
        [token]
    );

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        doSearch(e.target.value);
    };

    const handleSelectUser = (u: User) => {
        onClose();
        navigate(`/chat/@${u.user_name}`);
    };

    const handleNewGroup = () => {
        onClose();
        onNewGroup();
    };

    if (!rendered) return null;

    return (
        <>
            <div className={`ncp-backdrop ${entering ? "ncp-in" : ""}`} onClick={onClose} />

            <div className={`ncp-panel ${entering ? "ncp-in" : ""}`} role="dialog" aria-label="Nova conversa">
                {/* Header */}
                <div className="ncp-header">
                    <button type="button" className="ncp-back-btn" onClick={onClose} aria-label="Voltar">
                        <ArrowLeft size={18} />
                    </button>
                    <h5 className="ncp-title">{t("chat.newConversation")}</h5>
                </div>

                {/* Search */}
                <div className="ncp-search-wrap">
                    <Search size={14} className="ncp-search-icon" />
                    <input
                        ref={inputRef}
                        className="ncp-search-input"
                        placeholder={t("chat.searchNameOrUser")}
                        value={search}
                        onChange={handleSearch}
                    />
                </div>

                {/* Fixed option: new group */}
                <div className="ncp-option" onClick={handleNewGroup}>
                    <div className="ncp-option-icon">
                        <Users size={20} />
                    </div>
                    <span className="ncp-option-label">{t("chat.newGroup")}</span>
                </div>

                {/* Divider */}
                <div className="ncp-divider" />

                {/* Results */}
                <div className="ncp-results">
                    {loadingSearch ? (
                        <>
                            {[1, 2, 3].map(i => (
                                <div key={i} className="ncp-skeleton-row">
                                    <div className="ncp-skeleton ncp-skeleton-avatar" />
                                    <div style={{ flex: 1 }}>
                                        <div className="ncp-skeleton ncp-skeleton-line" style={{ width: "55%" }} />
                                        <div className="ncp-skeleton ncp-skeleton-line" style={{ width: "35%", marginTop: 5 }} />
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : results.length === 0 ? (
                        <p className="ncp-empty">
                            {search.trim() ? t("chat.noUsersFound") : t("chat.searchByNameOrUser")}
                        </p>
                    ) : (
                        results.map(u => (
                            <div key={u.id} className="ncp-user-row" onClick={() => handleSelectUser(u)}>
                                <img
                                    className="ncp-user-img"
                                    src={getImageUser(u.img_account)}
                                    onError={(e) => { e.currentTarget.src = getImageUserNotFound(u.name); }}
                                    alt={u.name}
                                />
                                <div className="ncp-user-info">
                                    <span className="ncp-user-name">{u.name}</span>
                                    <span className="ncp-user-sub">@{u.user_name}</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </>
    );
};

export default NewConversationPanel;
